import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  Modal,
  Fade,
  Paper,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { URL } from "../config";
import AddTechnicianModal from "./AddTechnician";
import toast from "react-hot-toast";

const TechnicianList = () => {
  const navigate = useNavigate();
  const { setLoading } = useOutletContext();
  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [techModalVisible, setTechModalVisible] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);

  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);


 const fetchTechnicians = async () => {
  setLoading(true);
  try {
    const response = await axios.get(`${URL}/technicians`);
    const data = response.data.map((item) => ({
      technicianId: item.technicianId,
      technicianName: item.technicianName,
      mobileNo: item.mobileNo,
      alternateMobileNo: item.alternateMobileNo,
      password: item.password,
      address: item.address,

      profilePhoto: item.profilePhoto
        ? `${URL}${item.profilePhoto}?t=${new Date().getTime()}`
        : null,

      aadharImg: item.aadharImg
        ? `${URL}${item.aadharImg}?t=${new Date().getTime()}`
        : null,

      otherImg: item.otherImg
        ? `${URL}${item.otherImg}?t=${new Date().getTime()}`
        : null,
    }));

    setDataSource((prev) => {
      const prevIds = prev.map((t) => t.technicianId).join(",");
      const newIds = data.map((t) => t.technicianId).join(",");
      if (prevIds !== newIds) return data;
      return prev;
    });
  } catch (error) {
    console.error("Error fetching technicians:", error);
  }
  setLoading(false);
};

useEffect(() => {
  fetchTechnicians();
}, []);


  const handleEdit = (technician) => {
    setSelectedTechnician(technician);
    setTechModalVisible(true);
  };


  const handleDelete = async (technicianId) => {
    if (!window.confirm("Are you sure you want to delete this technician?")) return;

    try {
      await axios.delete(`${URL}/technicians/${technicianId}`);
      toast.success("Technician deleted successfully!");
      setDataSource((prev) => prev.filter((t) => t.technicianId !== technicianId));
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete technician");
    }
  };


  const handleDownloadExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Technician List");

      worksheet.columns = [
        { header: "Sr No.", key: "technicianId", width: 15 },
        { header: "TechnicianName", key: "technicianName", width: 30 },
        { header: "MobileNo", key: "mobileNo", width: 20 },
        { header: "Alternate Mobile", key: "alternateMobileNo", width: 20 },
        { header: "Address", key: "address", width: 40 },
        { header: "Password", key: "password", width: 20 },
    
      ];

      dataSource.forEach((item) => {
        worksheet.addRow({
          technicianId: item.technicianId,
          technicianName: item.technicianName || "N/A",
          mobileNo: item.mobileNo || "N/A",
          alternateMobileNo: item.alternateMobileNo || "N/A",
          address: item.address || "",
          password: item.password || "",
        
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "Technician_List.xlsx");
    } catch (err) {
      console.error("Excel export error:", err);
    }
  };

 const handleDownloadPDF = () => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.setFontSize(18);
  doc.text("Technician List", 40, 40);

  const head = [
    ["Sr No.", "Technician Name", "Mobile No", "Alternate Mobile", "Address", "Password"],
  ];

  const body = dataSource.map((item, index) => [
    index + 1, 
    item.technicianName || "N/A",
    item.mobileNo || "N/A",
    item.alternateMobileNo || "N/A",
    item.address || "N/A",
    item.password || "N/A",
  ]);

  autoTable(doc, {
    startY: 60,
    head,
    body,
    headStyles: { fillColor: [23, 66, 83], textColor: "#fff", fontSize: 12 },
    styles: { fontSize: 10, cellPadding: 6, overflow: "linebreak" },
    theme: "grid",
    tableWidth: "auto", 
    didDrawPage: (data) => {
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const tableWidth = data.table.width;
      if (tableWidth < pageWidth - 40) {
        const extraWidth = (pageWidth - 40 - tableWidth) / head[0].length;
        data.table.columns.forEach((col) => {
          col.width += extraWidth;
        });
      }
    },
  });

  doc.save("Technician_List.pdf");
};


  const handlePrint = () => {
    const tableRows = dataSource
      .map(
        (item) => `
        <tr>
          <td>${item.technicianId}</td>
          <td>${item.technicianName || "N/A"}</td>
          <td>${item.mobileNo || "N/A"}</td>
          <td>${item.alternateMobileNo || "N/A"}</td>
          <td>${item.address || "N/A"}</td>
          <td>${item.password || "N/A"}</td>
        </tr>
      `
      )
      .join("");

    const style = `
      <style>
        @media print {
          @page { size: landscape; margin: 18mm; }
          body { -webkit-print-color-adjust: exact !important; }
        }
        table { width: 100%; border-collapse: collapse; font-size: 14px; }
        th { background: #174253; color: #fff; padding: 10px; text-align: left; font-size: 15px; border-bottom: 2px solid #174253; }
        td { padding: 10px; border-top: 1px solid #dcdcdc; border-bottom: 1px solid #dcdcdc; }
        tr:nth-child(even) td { background: #f5f5f5; }
        h1 { text-align: center; color: #174253; margin-bottom: 20px; font-size: 22px; }
      </style>
    `;

    const html = `
      <html>
        <head><title>Technician List</title>${style}</head>
        <body>
          <h1>Technician List</h1>
          <table>
            <thead>
              <tr>
                <th>Sr No.</th>
                <th>Technician Name</th>
                <th>Mobile No</th>
                <th>Alternate Mobile</th>
                <th>Address</th>
                <th>Password</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };


  const handleOpenMedia = (url) => {
    setSelectedMedia(url);
    setMediaModalOpen(true);
  };
  const handleCloseMedia = () => {
    setSelectedMedia(null);
    setMediaModalOpen(false);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "Actions",
        header: "Actions",
        size: 80,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => handleEdit(row.original)}>
              <EditIcon sx={{ color: "#174253" }} fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleDelete(row.original.technicianId)}>
              <DeleteIcon sx={{ color: "#d32f2f" }} fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
      { accessorKey: "technicianId", header: "ID", size: 70 },
      { accessorKey: "technicianName", header: "Technician Name", size: 180 },
      { accessorKey: "mobileNo", header: "Mobile No", size: 140 },
      { accessorKey: "alternateMobileNo", header: "Alt. Mobile", size: 140 },
      {
        accessorKey: "address",
        header: "Address",
        size: 220,
        Cell: ({ row }) => (
          <span
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: "1.4em",
            }}
            title={row.original.address}
          >
            {row.original.address}
          </span>
        ),
      },
      {
        accessorKey: "password",
        header: "Password",
        size: 150,
        Cell: ({ row }) => <span style={{ letterSpacing: "2px" }}>{row.original.password}</span>,
      },
       {
        accessorKey: "profilePhoto",
        header: "Profile",
        size: 100,
        Cell: ({ row }) =>
          row.original.profilePhoto ? (
            <img
              src={row.original.profilePhoto}
              alt="Profile"
              onClick={() => handleOpenMedia(row.original.profilePhoto)}
              style={{
                width: 45,
                height: 45,
                borderRadius: "50%",
                objectFit: "cover",
                cursor: "pointer",
                border: "1px solid #ccc",
              }}
            />
          ) : (
            <span style={{ color: "#888" }}>N/A</span>
          ),
      },
      
    ],
    []
  );


  return (
    <Box sx={{ width: "100%", bgcolor: "#fff", marginTop: "20px", p: { xs: 1, md: 0 }, overflow: "hidden" }}>
   
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, paddingRight: "20px" }}>
        <Box sx={{ display: "flex", alignItems: "center", fontWeight: 700, fontSize: "22px", color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", paddingLeft: "12px" }}>
          Technician List
        </Box>
        <Button
          type="primary"
          onClick={() => {
            setSelectedTechnician(null);
            setTechModalVisible(true);
          }}
          sx={{ borderRadius: 25, background: "linear-gradient(90deg, #0b8793, #174253)", color: "white", border: "none", padding: "8px 16px", "&:hover": { background: "linear-gradient(90deg, #174253, #0b8793)" } }}
        >
          Add Technician
        </Button>
      </Box>

      <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
        <div style={{ position: "relative", width: "100%", marginTop: "10px" }}>
          <div style={{ position: "absolute", top: 10, right: 20, left: 20, display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Tooltip title="Download Excel">
                <IconButton onClick={handleDownloadExcel} sx={{ backgroundColor: "#d4f1d4", color: "#174253", "&:hover": { backgroundColor: "#b5e3b5" } }}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download PDF">
                <IconButton onClick={handleDownloadPDF} sx={{ backgroundColor: "#ffe2e2", color: "#d32f2f", "&:hover": { backgroundColor: "#ffcccc" } }}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton onClick={handlePrint} sx={{ backgroundColor: "#e3f2fd", color: "#174253", "&:hover": { backgroundColor: "#cde4fa" } }}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </div>
            <TextField
              size="small"
              placeholder="Search technicians..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#174253" }} /></InputAdornment> }}
              sx={{ width: { xs: 200, sm: 250 }, "& .MuiOutlinedInput-root": { borderRadius: 25, height: 38, backgroundColor: "#fff", boxShadow: "0 0 6px rgba(0,0,0,0.1)", "& fieldset": { borderColor: "#d0d7de" }, "&:hover fieldset": { borderColor: "#174253" } } }}
            />
          </div>
        </div>

        <MaterialReactTable
  columns={columns}
  data={
    [...dataSource] 
      .reverse()  
      .filter((item) =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(globalFilter.toLowerCase())
      )
  }

  enableColumnActions={false}
  enableDensityToggle={false}
  enableFullScreenToggle={false}
  enableColumnFilters={false}
  enableHiding={false}
  enableGlobalFilter={false} 

  muiTablePaperProps={{
    sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" },
  }}

  muiTableContainerProps={{
    sx: {
      backgroundColor: "#fff",
      width: "100%",
      overflowX: "auto",
      borderRadius: "12px",
      "&::-webkit-scrollbar": { height: "6px" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#e0e3ea",
        borderRadius: "10px",
      },
    },
  }}

  muiTableHeadCellProps={{
    sx: {
      backgroundColor: "#e0e0e0ff",
      color: "#393e4f",
      fontWeight: 600,
      fontSize: "16px",
      borderBottom: "0.5px solid #e3e5eb",
      borderRight: "none",
      borderLeft: "none",
      textAlign: "left",
      height: "63px",
      paddingTop: "23px",
      marginTop: "15px",
    },
  }}

 muiTableBodyRowProps={({ row }) => ({
  onClick: () => {
    navigate(`/dashboard/technicians/${row.original.technicianId}`);
  },
  sx: {
    cursor: "pointer",
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "#e3f2fd",
    },
    "& td": {
      fontSize: { xs: "12px", sm: "14px" },
      p: "8px",
      borderBottom: "0.5px solid #e3e5eb",
      height: "54px",
    },
  },
})}


  initialState={{
    pagination: {
      pageIndex: 0,
      pageSize: 100, 
    },
  }}
/>

      </Box>

     
      <Modal
  open={techModalVisible}
  onClose={() => {
    setTechModalVisible(false);
    setSelectedTechnician(null);
  }}
  closeAfterTransition
  BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.6)" } }}
>
  <Fade in={techModalVisible}>
    <Paper sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", p: 0, m: 0, borderRadius: 0, boxShadow: "none", overflowY: "auto", outline: "none", bgcolor: "transparent", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <AddTechnicianModal
        technician={selectedTechnician}
        onClose={() => {
          setTechModalVisible(false);
          setSelectedTechnician(null);
        }}
        onSaveComplete={(updatedTech) => {
        
          setDataSource((prev) => {
            const index = prev.findIndex(t => t.technicianId === updatedTech.technicianId);
            if (index !== -1) {
              const newData = [...prev];
              newData[index] = updatedTech; 
              return newData;
            }
            return [updatedTech, ...prev]; 
          });
        }}
      />
    </Paper>
  </Fade>
</Modal>

      <Modal
        open={mediaModalOpen}
        onClose={handleCloseMedia}
        closeAfterTransition
        BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.6)" } }}
      >
        <Fade in={mediaModalOpen}>
          <Paper sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "transparent", outline: "none" }}>
            <Box sx={{ position: "relative", maxWidth: "90%", maxHeight: "90%" }}>
              <IconButton
                onClick={handleCloseMedia}
                sx={{ position:"absolute", top:8, right:8, color:"#000", zIndex:10 }}
              >
                <CloseIcon />
              </IconButton>
              {selectedMedia && (
                <img
                  src={selectedMedia}
                  alt="Preview"
                  style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8 }}
                />
              )}
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};

export default TechnicianList;
